"use client";

import type { QuestViewData } from "@/components/QuestsV2/types";
import { usePassportProvider } from "@/context";
import { Action, useAnalytics } from "@/context/AnalyticsProvider";
import { useLoginModal } from "@/context/LoginModalContext";
import { useReferralStatus } from "@/hooks/useReferralStatus";
import {
  Badge,
  Body,
  Box,
  BulletList,
  ButtCon,
  Button,
  Heading,
  Icon,
  Modal,
  Stack,
} from "@biom3/react";
import { MODAL_HEIGHT, MODAL_WIDTH } from "@constants";
import { useReferralAcceptance } from "@hooks/useReferralAcceptance";
import type { Content } from "@prismicio/client";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useMemo } from "react";

type ReferralAcceptanceModalProps = {
  visible: boolean;
  referralCode: string | null;
  questViewData: QuestViewData;
  gameData: Content.GameDocument["data"];
  setShowReferralAccept: (visible: boolean) => void;
};

export function ReferralAcceptanceModal({
  visible,
  referralCode,
  questViewData,
  gameData,
  setShowReferralAccept,
}: ReferralAcceptanceModalProps) {
  const { isLoggedIn } = usePassportProvider();
  const { trackV3 } = useAnalytics();
  const { handleAcceptReferral, error, isAccepting } = useReferralAcceptance();
  const { openLoginModal } = useLoginModal();

  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const campaignId = questViewData?.visibleCampaignContent?.campaign?.id;
  const allQuests =
    questViewData?.campaignQuestGroups?.flatMap((g) => g.quests) ?? [];
  const refereeQuest = allQuests.find((q) => q.type === "campaign_referee");
  const referrerQuest = allQuests.find((q) => q.type === "campaign_referrer");
  const bonusLabel =
    refereeQuest?.campaignPoints ?? referrerQuest?.campaignPoints ?? "";
  const titleGame = gameData?.name ?? "the";
  const gameId = (gameData?.games_service_item?.id as string) || undefined;

  const { referralStatus } = useReferralStatus(campaignId);

  const qualifyingExternalId = referralStatus?.qualifying_quest_id;
  const qualifyingQuestName = useMemo(() => {
    if (!qualifyingExternalId) return undefined;
    return (
      allQuests.find((q) => q.externalId === qualifyingExternalId)?.name ??
      "the quest"
    );
  }, [qualifyingExternalId, allQuests]);

  const displayedError = useMemo(() => {
    if (!error) return "";
    const normalized = String(error).trim();
    if (normalized === "This reward is only available to new users.") {
      return "only available to new leaderboard participants";
    }
    return "Unable to accept invite. Please try again.";
  }, [error]);

  const clearReferralQuery = useCallback(() => {
    const params = new URLSearchParams(searchParams?.toString());
    params.delete("referral");
    router.replace(
      `${pathname}${params.toString() ? `?${params.toString()}` : ""}`,
    );
  }, [pathname, router, searchParams]);

  const onAcceptReferral = useCallback(() => {
    if (!referralCode) return;

    trackV3({
      object: "Quest",
      action: Action.PRESSED,
      extras: {
        campaignId,
        gameId,
        section: "ReferralAcceptanceModal",
        control: "AcceptInvite",
        referralCode: referralCode ?? undefined,
        isLoggedIn: Boolean(isLoggedIn),
      },
    });

    const handleAccept = async () => {
      const success = await handleAcceptReferral(
        referralCode,
        typeof campaignId === "number" ? campaignId : undefined,
      );
      if (success) {
        setShowReferralAccept(false);
        clearReferralQuery();
      }
    };

    if (!isLoggedIn) {
      openLoginModal(handleAccept);
      return;
    }
    void handleAccept();
  }, [
    handleAcceptReferral,
    isLoggedIn,
    openLoginModal,
    clearReferralQuery,
    campaignId,
    gameId,
    referralCode,
    setShowReferralAccept,
    trackV3,
  ]);

  const handleClose = () => {
    trackV3({
      object: "Quest",
      action: Action.PRESSED,
      extras: {
        campaignId,
        gameId,
        section: "ReferralAcceptanceModal",
        control: "CloseReferralAcceptanceModal",
        referralCode: referralCode ?? undefined,
        isLoggedIn: Boolean(isLoggedIn),
      },
    });
    setShowReferralAccept(false);
    clearReferralQuery();
  };

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      showBgOverlay={false}
      outsideClicksClose={false}
      testId="referral-acceptance-modal"
    >
      <Modal.Content
        sx={{
          d: "flex",
          flexDirection: "column",
          justifyContent: "center",
          borderRadius: "base.borderRadius.x10",
          position: "relative",
          overflow: "hidden",
          background:
            "var(--color-translucent-inverse-600, rgba(13, 13, 13, 0.48))",
          backdropFilter: "blur(28px)",
          height: MODAL_HEIGHT,
          width: {
            default: "calc(100dvw - 40px)",
            small: MODAL_WIDTH,
          },
        }}
      >
        {/* Content Area */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "base.spacing.x14",
            height: "100%",
            justifyContent: "space-between",
          }}
        >
          {/* Text Content */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              px: "base.spacing.x12",
              paddingTop: "base.spacing.x24",
              alignItems: "center",
              gap: "base.spacing.x4",
            }}
          >
            <Heading size="medium" weight="bold" sx={{ textAlign: "center" }}>
              You've been invited to join {titleGame} leaderboard and win prizes
            </Heading>
            <Badge
              variant="dark"
              badgeContent={
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  gap="base.spacing.x1"
                >
                  <Body size={"small"}>BONUS+ {bonusLabel ?? "xx"}</Body>
                  <Icon variant="bold" icon="Sparkle" />
                </Stack>
              }
            />
            <BulletList>
              <BulletList.Item size="small">
                Complete '{qualifyingQuestName}' to get your {bonusLabel} bonus
                points and join the leaderboard
              </BulletList.Item>
              <BulletList.Item size="small">
                Complete more quests and climb the ranks
              </BulletList.Item>
              <BulletList.Item size="small">
                Top scorers will be eligible to receive rewards from the prize
                pool
              </BulletList.Item>
            </BulletList>
          </Box>
          <Box
            sx={{
              mx: "base.spacing.x6",
              mb: "base.spacing.x12",
            }}
          >
            {error && (
              <Box
                sx={{
                  d: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "base.spacing.x2",
                }}
              >
                <Icon
                  icon="Alert"
                  sx={{
                    fill: "var(--color-text-status-fatal-primary, #FF637F)",
                  }}
                />
                <Body
                  size="small"
                  sx={{
                    d: "flex",
                    color: "var(--color-text-status-fatal-primary, #FF637F)",
                    textAlign: "center",
                    justifyContent: "center",
                  }}
                >
                  {displayedError}
                </Body>
              </Box>
            )}
            <Button
              variant="primary"
              size="large"
              onClick={() => {
                trackV3({
                  object: "Quest",
                  action: Action.PRESSED,
                  extras: {
                    section: "ReferralAcceptanceModal",
                    control: error ? "ContinueAfterError" : "AcceptInvite",
                    referralCode: referralCode ?? undefined,
                    campaignId,
                    gameId,
                    isLoggedIn: Boolean(isLoggedIn),
                  },
                });
                if (error) {
                  handleClose();
                } else {
                  onAcceptReferral();
                }
              }}
              disabled={!referralCode || isAccepting}
              sx={{
                width: "100%",
              }}
            >
              {isAccepting ? (
                <Button.Icon icon="Loading" />
              ) : error ? (
                "Continue"
              ) : (
                "Accept Invite"
              )}
            </Button>
          </Box>
        </Box>

        {/* Close button */}
        <ButtCon
          icon="Close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: "base.spacing.x4",
            right: "base.spacing.x4",
          }}
          variant="tertiary"
        />
      </Modal.Content>
    </Modal>
  );
}
